import React from 'react';
import { useTranslation } from 'react-i18next';

function TypeBadge({ colorClass, label }) {
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white ${colorClass || 'bg-gray-400'}`}>
      {label}
    </span>
  );
}

function StatusBadge({ kind, label }) {
  const cls =
    kind === 'completed'
      ? 'bg-green-100 text-green-800'
      : kind === 'missed'
        ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${cls}`}>
      {label}
    </span>
  );
}

export default function DayWorkoutsChooserModal({
  isOpen,
  onClose,
  dayName,
  dateISO,
  workouts,
  workoutColors,
  canEdit,
  onSelectWorkout,
  onAddWorkout
}) {
  const { t } = useTranslation();
  const list = Array.isArray(workouts) ? workouts : [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4 z-50">
      <div className="bg-white w-full md:max-w-xl rounded-t-2xl md:rounded-xl shadow-2xl overflow-hidden max-h-[92vh] md:max-h-[90vh] flex flex-col">
        <div className="p-4 md:p-6 border-b border-gray-100 flex justify-between items-start gap-3">
          <div className="min-w-0">
            <div className="text-xs font-extrabold uppercase tracking-wide text-gray-500">
              {t('plan.training_calendar')}
            </div>
            <div className="mt-1 font-extrabold text-gray-900 truncate">
              {dayName || ''}{dateISO ? ` · ${dateISO}` : ''}
            </div>
            <div className="mt-1 text-sm text-gray-600">
              {t('plan.selected_workout_panel.choose_session', { defaultValue: 'Choose a session to view.' })}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 px-3 py-1"
            aria-label={t('workout.close')}
          >
            ✕
          </button>
        </div>

        <div className="p-4 md:p-6 overflow-y-auto space-y-2">
          {list.map((w) => {
            const type = String(w?.type || '').replace(/_/g, ' ') || 'workout';
            const isDone = w?.completed === true;
            const isMissed = w?.skipped === true && !isDone;
            return (
              <button
                key={w?.id || `${w?.date || ''}-${type}`}
                type="button"
                onClick={() => {
                  if (typeof onSelectWorkout === 'function') onSelectWorkout(w);
                }}
                className="w-full text-left bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 active:scale-[0.99] transition p-3"
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-3 h-3 rounded-full ${workoutColors?.[w?.type] || 'bg-gray-300'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-2 items-center">
                      <TypeBadge colorClass={workoutColors?.[w?.type]} label={type} />
                      {isDone ? <StatusBadge kind="completed" label={t('plan.completed')} /> : null}
                      {isMissed ? <StatusBadge kind="missed" label={t('plan.missed')} /> : null}
                    </div>
                    <div className="mt-1 font-extrabold text-gray-900 truncate">
                      {w?.title || '(Untitled)'}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-4 md:p-6 border-t border-gray-100 bg-white flex flex-col md:flex-row gap-2 md:gap-3 md:justify-end">
          {canEdit && typeof onAddWorkout === 'function' ? (
            <button
              type="button"
              onClick={onAddWorkout}
              className="px-4 py-2 rounded-lg font-extrabold bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {t('plan.selected_workout_panel.add_another')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

